import { useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { formsApi } from '@/api/formsApi'
import { patientsApi } from '@/api/patientsApi'
import { productsApi } from '@/api/productsApi'
import { proceduresApi } from '@/api/proceduresApi'
import { equipmentApi, type ApiMaintenanceEvent } from '@/api/equipmentApi'
import { groupAuditByForm } from '@/lib/board/auditTrail'
import { boardServerQuery, type BoardFilters } from '@/lib/board/boardFilters'
import { boardShowsCleaningTasks } from '@/lib/board/boardTasks'
import { useTenant } from '@/providers/TenantProvider'
import type { AuditTrailRow } from '@/types/audit'

const BOARD_KEY = 'procedure-board'
const PATIENT_RUNS_KEY = 'patient-runs'
const AUDIT_CONCURRENCY = 5

/**
 * Every run matching the board's server-side filters, with its summary.
 *
 * One request for the whole board: the summary carries progress, holds, CAPA
 * and P/H counts, so no card fetches anything on its own. Unpaged, because
 * "open" is derived from steps and can only be filtered after the fact.
 */
export function useProcedureBoard(filters: BoardFilters) {
  const { selectedTenantId } = useTenant()
  const server = boardServerQuery(filters)
  return useQuery({
    queryKey: [BOARD_KEY, selectedTenantId, server],
    enabled: !!selectedTenantId,
    queryFn: () => proceduresApi.listAll({ ...server, summary: true }),
  })
}

/** The most cleaning tasks of one kind the board reads: the latest by due date. */
const BOARD_TASK_LIMIT = 1000

const ROOMS = ['ROOM'] as const

/**
 * The cleaning tasks the board shows beside its runs (z8nrz7f9u5), read from
 * the Tasks feed. Open work is everything due up to today plus whatever is
 * started; finished work only when the status filter asks for it. Keyed under
 * 'maintenance-events', so a Start, End or assignment anywhere refreshes it.
 *
 * Off for someone who cannot read cleaning tasks, and when the filters leave
 * no room for them (another type, a patient or a customer).
 */
export function useBoardCleaningTasks(filters: BoardFilters, today: string, canRead: boolean) {
  const { selectedTenantId } = useTenant()
  const wantsOpen = !['complete', 'cancelled', 'pending_review'].includes(filters.status)
  const wantsDone = filters.status === 'complete' || filters.status === 'all'
  const enabled = !!selectedTenantId && canRead && boardShowsCleaningTasks(filters) && (wantsOpen || wantsDone)
  const tomorrow = new Date(`${today}T00:00:00Z`)
  tomorrow.setUTCDate(tomorrow.getUTCDate() + 1)
  const to = tomorrow.toISOString().slice(0, 10)
  return useQuery({
    queryKey: ['maintenance-events', selectedTenantId, 'board', today, wantsOpen, wantsDone],
    enabled,
    queryFn: async (): Promise<ApiMaintenanceEvent[]> => {
      const tenant = selectedTenantId as string
      const [notStarted, started, done] = await Promise.all([
        wantsOpen
          ? equipmentApi.listEvents(tenant, { purposes: ROOMS, status: 'NOT_STARTED', to, limit: BOARD_TASK_LIMIT })
          : [],
        wantsOpen ? equipmentApi.listEvents(tenant, { purposes: ROOMS, status: 'STARTED', limit: BOARD_TASK_LIMIT }) : [],
        wantsDone ? equipmentApi.listEvents(tenant, { purposes: ROOMS, status: 'COMPLETED', limit: BOARD_TASK_LIMIT }) : [],
      ])
      return [...notStarted, ...started, ...done]
    },
  })
}

export function useInvalidateProcedureBoard() {
  const qc = useQueryClient()
  return useCallback(() => qc.invalidateQueries({ queryKey: [BOARD_KEY] }), [qc])
}

/** Patients for the board's patient filter and card names. */
export function useBoardPatients() {
  const { selectedTenantId } = useTenant()
  return useQuery({
    queryKey: ['patients', 'board-options', selectedTenantId],
    enabled: !!selectedTenantId,
    queryFn: () => patientsApi.list({ take: 1000 }).then(r => r.items),
  })
}

/** Everything one run produced, for the P dialog. */
export function useRunProducts(procedureId: string | null) {
  const { selectedTenantId } = useTenant()
  return useQuery({
    queryKey: ['products', 'created', selectedTenantId, 'procedure', procedureId],
    enabled: !!selectedTenantId && !!procedureId,
    queryFn: () => productsApi.listCreatedProducts(selectedTenantId as string, { procedureId: procedureId as string }),
  })
}

/** The patient's other runs, for the H panel. */
export function usePatientRuns(patientMrn: string | null, excludeProcedureId: string | null) {
  const { selectedTenantId } = useTenant()
  return useQuery({
    queryKey: [PATIENT_RUNS_KEY, selectedTenantId, patientMrn],
    enabled: !!selectedTenantId && !!patientMrn,
    // The panel shows step progress, which moves whenever anyone works a run:
    // a remount asks again, while the rows already held stay on screen.
    staleTime: 0,
    // Each row's progress in the same response, counted per run, instead of a
    // journey request per transplant (z8nrz7fzk7).
    queryFn: () => proceduresApi.listForPatient(patientMrn as string, { progress: 'run' }).then(r => r.items),
    select: items => items.filter(p => p.id !== excludeProcedureId),
  })
}

/**
 * A run's audit trail, form by form, fetched only while the dialog is open.
 *
 * Built from the existing form-level trail: one list of the run's forms, then
 * each form's entries. There is no procedure-level audit endpoint.
 */
export function useRunAuditTrail(procedureId: string | null) {
  const { selectedTenantId } = useTenant()
  return useQuery({
    queryKey: [BOARD_KEY, 'audit', selectedTenantId, procedureId],
    enabled: !!selectedTenantId && !!procedureId,
    staleTime: 0,
    queryFn: async () => {
      const submissions = await formsApi.listProcedureSubmissions(procedureId as string)
      // Five at a time: a long run would otherwise spend a burst of the
      // per-user request budget on one click.
      const trails: AuditTrailRow[][] = []
      for (let i = 0; i < submissions.length; i += AUDIT_CONCURRENCY) {
        const batch = submissions.slice(i, i + AUDIT_CONCURRENCY)
        trails.push(...(await Promise.all(batch.map(s => formsApi.getAuditTrail(s.id)))))
      }
      return groupAuditByForm(submissions, trails)
    },
  })
}
